import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Loader2, Trash2, ArrowRight, ArrowLeft, CheckCircle, MapPin, User, Phone, 
  Shield, Clock, Info, AlertTriangle, Package
} from 'lucide-react';
import { trackBite, trackOTPSent } from '../components/Analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom fetch that preserves response body for error cases
// The Emergent script patches window.fetch and reads response.text() for error logging
// This consumes the body before our code can access it
const safeFetch = async (url, options) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options?.method || 'GET', url, true);
    
    // Set headers
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    
    xhr.onload = function() {
      const responseObj = {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        text: () => Promise.resolve(xhr.responseText),
        json: () => Promise.resolve(JSON.parse(xhr.responseText)),
        headers: new Headers()
      };
      resolve(responseObj);
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(options?.body);
  });
};

// Safe response parser
const safeParseResponse = async (response) => {
  try {
    const text = await response.text();
    if (text) {
      try {
        return JSON.parse(text);
      } catch {
        return { detail: text };
      }
    }
    return { detail: 'Empty response' };
  } catch (err) {
    console.error('Response parsing error:', err);
    return { detail: 'Unable to process response' };
  }
};

const WasteBookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  
  // Service data (loaded after OTP verification)
  const [service, setService] = useState(null);
  const [serviceLevels, setServiceLevels] = useState([]);
  const [palletPricing, setPalletPricing] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  
  // Subscription client data
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Service level
    serviceLevelId: '',
    // Client details (person booking)
    clientName: '',
    clientBusinessName: '',
    clientMobile: '',
    clientEmail: '',
    otp: '',
    // Customer details (person at site we contact)
    customerName: '',
    customerMobile: '',
    siteAddress: '',
    siteNotes: '',
    // Pallets
    palletsPresent: false,
    palletsQty: 1
  });
  
  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const addressDebounceRef = useRef(null);
  const sessionTokenRef = useRef(crypto.randomUUID());
  
  // Logged-in client state (skip OTP if logged in)
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
  const [loggedInClient, setLoggedInClient] = useState(null);

  // Check if client is logged in on mount
  useEffect(() => {
    const checkClientLogin = async () => {
      const clientToken = localStorage.getItem('client_token');
      if (clientToken) {
        try {
          const response = await safeFetch(`${API_URL}/api/client-portal/me`, {
            headers: { 'Authorization': `Bearer ${clientToken}` }
          });
          if (response.ok) {
            const data = await safeParseResponse(response);
            setIsClientLoggedIn(true);
            setLoggedInClient(data.client);
            // Auto-fill client details
            setFormData(prev => ({
              ...prev,
              clientName: data.client.name || '',
              clientBusinessName: data.client.business_name || '',
              clientMobile: data.client.mobile || '',
              clientEmail: data.client.email || ''
            }));
          }
        } catch (err) {
          console.error('Failed to verify client token:', err);
        }
      }
    };
    checkClientLogin();
  }, []);

  // Fetch service data AFTER OTP verification
  const fetchServiceData = async () => {
    setLoading(true);
    try {
      const [serviceRes, levelsRes] = await Promise.all([
        fetch(`${API_URL}/api/services/waste_removal`),
        fetch(`${API_URL}/api/service-levels/`)
      ]);
      
      if (serviceRes.ok) {
        const serviceData = await serviceRes.json();
        setService(serviceData);
        
        // Fetch pallet pricing
        const palletRes = await safeFetch(`${API_URL}/api/services/${serviceData.id}/pallet-pricing`);
        if (palletRes.ok) {
          setPalletPricing(await palletRes.json());
        }
        
        // Filter levels for this service
        if (levelsRes.ok) {
          const allLevels = await levelsRes.json();
          const wasteLevels = allLevels.filter(l => l.service_id === serviceData.id);
          setServiceLevels(wasteLevels.sort((a, b) => a.display_order - b.display_order));
        }
      }
    } catch (err) {
      console.error('Failed to fetch service data:', err);
      setError('Failed to load service information');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const selectServiceLevel = (level) => {
    setSelectedLevel(level);
    handleChange('serviceLevelId', level.id);
    // Reset subscription status when changing level
    setSubscriptionStatus(null);
  };

  // Check if client is a subscription client when they select Subscription level
  const checkSubscriptionStatus = async () => {
    if (!formData.clientMobile || formData.clientMobile.length < 10) {
      return null;
    }
    
    setCheckingSubscription(true);
    
    try {
      const response = await safeFetch(`${API_URL}/api/subscription-clients/check/${formData.clientMobile}`);
      const data = await safeParseResponse(response);
      setSubscriptionStatus(data);
      return data;
    } catch (err) {
      console.error('Failed to check subscription:', err);
      return null;
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.clientName || !formData.clientMobile) {
      setError('Please enter your name and mobile number');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    // If client is logged in, skip OTP and go directly to verified state
    if (isClientLoggedIn) {
      // Load service data and check subscription status
      await fetchServiceData();
      const subStatus = await checkSubscriptionStatus();
      
      // If user is subscription client or authorized user, auto-select subscription level
      if (subStatus && (subStatus.is_subscription_client || subStatus.is_authorized_user) && subStatus.is_active) {
        const subscriptionLevel = serviceLevels.find(l => l.billing_type === 'WEEKLY_SUBSCRIPTION');
        if (subscriptionLevel) {
          selectServiceLevel(subscriptionLevel);
        }
      }
      
      setStep(2); // Skip OTP step, go to site details
      setIsSubmitting(false);
      return;
    }
    
    // Track OTP for follow-up (only for non-logged-in users)
    trackOTPSent(formData.clientMobile, 'waste_booking', formData.clientName, formData.clientEmail, null);
    
    try {
      const response = await safeFetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formData.clientMobile })
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok && data.success) {
        setStep(1);
      } else {
        setError(data.detail || data.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleVerifyOTP = async () => {
    setIsVerifying(true);
    setError('');
    
    try {
      const response = await safeFetch(`${API_URL}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone_number: formData.clientMobile,
          code: formData.otp 
        })
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok && data.valid) {
        // Load service data and check subscription status (including authorized users)
        await fetchServiceData();
        const subStatus = await checkSubscriptionStatus();
        
        // If user is subscription client or authorized user, auto-select subscription level
        if (subStatus && (subStatus.is_subscription_client || subStatus.is_authorized_user) && subStatus.is_active) {
          // Pre-select the subscription level
          const subscriptionLevel = serviceLevels.find(l => l.billing_type === 'WEEKLY_SUBSCRIPTION');
          if (subscriptionLevel) {
            selectServiceLevel(subscriptionLevel);
          }
        }
        
        setStep(2);
      } else {
        setError(data.message || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    }
    
    setIsVerifying(false);
  };

  const handleSelectLevel = async () => {
    if (!selectedLevel) {
      setError('Please select a service level');
      return;
    }
    
    // Check subscription eligibility if subscription level selected
    if (selectedLevel.billing_type === 'WEEKLY_SUBSCRIPTION') {
      const status = subscriptionStatus || await checkSubscriptionStatus();
      
      // Check if user is subscription client OR authorized user
      if (!status?.is_subscription_client && !status?.is_authorized_user) {
        setError('This mobile number is not registered as a subscription client or authorized user. Please use Standard or Priority service level, or contact Transa to set up a subscription.');
        return;
      }
      
      if (!status?.is_active) {
        setError('The subscription is currently inactive. Please contact Transa to reactivate, or use Standard or Priority service level.');
        return;
      }
      
      if (status?.is_over_quota) {
        setError(`Weekly booking quota has been reached (${status.bookings_used}/${status.effective_limit} used). Please contact Transa to upgrade your plan, or use Priority for this booking.`);
        return;
      }
    }
    
    setStep(3);
  };

  const handleAddressInput = (value) => {
    handleChange('siteAddress', value);
    
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    
    if (value.length > 3) {
      setIsLoadingAddresses(true);
      
      addressDebounceRef.current = setTimeout(async () => {
        try {
          const response = await safeFetch(
            `${API_URL}/api/places/autocomplete?input=${encodeURIComponent(value)}&sessiontoken=${sessionTokenRef.current}`
          );
          
          if (response.ok) {
            const data = await safeParseResponse(response);
            setAddressSuggestions(data.predictions || []);
            setShowSuggestions(data.predictions?.length > 0);
          }
        } catch (err) {
          console.error('Address lookup error:', err);
        }
        setIsLoadingAddresses(false);
      }, 300);
    } else {
      setShowSuggestions(false);
      setIsLoadingAddresses(false);
    }
  };

  const selectAddress = (suggestion) => {
    handleChange('siteAddress', suggestion.description);
    setShowSuggestions(false);
    sessionTokenRef.current = crypto.randomUUID();
  };

  const calculateTotal = () => {
    if (!selectedLevel) return 0;
    
    let total = selectedLevel.base_price;
    
    if (formData.palletsPresent && palletPricing?.enabled) {
      if (palletPricing.pricing_mode === 'PER_PALLET') {
        total += palletPricing.price * (formData.palletsQty || 1);
      } else {
        total += palletPricing.price;
      }
    }
    
    return total;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // Use safeFetch to bypass Emergent's fetch interceptor that consumes response body
      const response = await safeFetch(`${API_URL}/api/v2/bookings/waste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_level_id: formData.serviceLevelId,
          client_name: formData.clientName,
          client_business_name: formData.clientBusinessName,
          client_mobile: formData.clientMobile,
          client_email: formData.clientEmail,
          customer_name: formData.customerName,
          customer_mobile: formData.customerMobile,
          site_address: formData.siteAddress,
          site_notes: formData.siteNotes,
          pallets_present: formData.palletsPresent,
          pallets_qty: formData.palletsPresent ? formData.palletsQty : null
        })
      });
      
      const booking = await safeParseResponse(response);
      
      if (!response.ok) {
        throw new Error(booking.detail || 'Failed to create booking');
      }
      
      navigate(`/booking/success?ref=${booking.booking_ref}&service=waste`);
    } catch (err) {
      setError(err.message || 'Failed to create booking. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // Step labels for progress indicator
  const steps = ['Your Details', 'Verify', 'Select Level', 'Customer', 'Pallets', 'Confirm'];

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0D9488] to-[#14B8A6] flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#111111]">Book Waste Removal</h1>
          <p className="text-[#64748B] mt-1">We collect and dispose of installation packaging waste</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((label, idx) => (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-1 ${idx <= step ? 'text-[#0D9488]' : 'text-[#94A3B8]'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  idx < step ? 'bg-[#0D9488] text-white' : idx === step ? 'bg-[#0D9488] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
                }`}>
                  {idx < step ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <span className="text-xs hidden sm:inline">{label}</span>
              </div>
              {idx < 5 && <div className={`w-6 h-0.5 ${idx < step ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Step 0: Client Details */}
        {step === 0 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                  <User className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Your Details</h2>
                  <p className="text-sm text-[#64748B]">
                    {isClientLoggedIn 
                      ? 'Logged in from Client Portal - no verification needed' 
                      : "We'll verify your number before showing pricing"}
                  </p>
                </div>
              </div>

              {/* Logged-in banner */}
              {isClientLoggedIn && (
                <div className="mb-4 p-3 bg-[#F0FDFA] border border-[#0D9488]/20 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                  <div>
                    <p className="text-sm font-medium text-[#0D9488]">Logged in as {loggedInClient?.name}</p>
                    <p className="text-xs text-[#64748B]">{loggedInClient?.business_name} • OTP verification not required</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Your Name *</label>
                  <Input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => handleChange('clientName', e.target.value)}
                    placeholder="John Smith"
                    className="border-[#E2E8F0] h-12"
                    data-testid="client-name-input"
                    disabled={isClientLoggedIn}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Business Name</label>
                  <Input
                    type="text"
                    value={formData.clientBusinessName}
                    onChange={(e) => handleChange('clientBusinessName', e.target.value)}
                    placeholder="Smith Installations Pty Ltd"
                    className="border-[#E2E8F0] h-12"
                    disabled={isClientLoggedIn}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Mobile Number *</label>
                  <Input
                    type="tel"
                    value={formData.clientMobile}
                    onChange={(e) => handleChange('clientMobile', e.target.value)}
                    placeholder="04XX XXX XXX"
                    className="border-[#E2E8F0] h-12"
                    data-testid="client-mobile-input"
                    disabled={isClientLoggedIn}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Email <span className="text-[#94A3B8] font-normal">(for confirmation)</span></label>
                  <Input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => handleChange('clientEmail', e.target.value)}
                    placeholder="john@example.com"
                    className="border-[#E2E8F0] h-12"
                    disabled={isClientLoggedIn}
                  />
                </div>
              </div>

              <Button 
                className="w-full mt-6 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                onClick={handleSendOTP}
                disabled={!formData.clientName || !formData.clientMobile || isSubmitting}
                data-testid="send-otp-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isClientLoggedIn ? (
                  <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>
                ) : (
                  <>Verify My Number <ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: OTP Verification */}
        {step === 1 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Verify Your Number</h2>
                  <p className="text-sm text-[#64748B]">Code sent to {formData.clientMobile}</p>
                </div>
              </div>

              <div className="space-y-4">
                <Input
                  type="text"
                  value={formData.otp}
                  onChange={(e) => handleChange('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="border-[#E2E8F0] h-14 text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="otp-input"
                />
                <p className="text-center text-sm text-[#64748B]">
                  Didn't receive it? <button onClick={handleSendOTP} className="text-[#0D9488] hover:underline">Resend</button>
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={goBack} className="border-[#E2E8F0]">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                  onClick={handleVerifyOTP}
                  disabled={formData.otp.length < 4 || isVerifying}
                  data-testid="verify-otp-btn"
                >
                  {isVerifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Service Level (AFTER verification) */}
        {step === 2 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Select Service Level</h2>
                  <p className="text-sm text-[#64748B]">Choose the option that suits your needs</p>
                </div>
              </div>

              {/* Subscription Status Banner - for both direct clients and authorized users */}
              {subscriptionStatus?.is_active && (subscriptionStatus?.is_subscription_client || subscriptionStatus?.is_authorized_user) && (
                <div className="mb-6 p-4 bg-[#F0FDFA] border border-[#99F6E4] rounded-lg">
                  <div className="flex items-center gap-2 text-[#0D9488] font-medium">
                    <CheckCircle className="w-5 h-5" />
                    {subscriptionStatus.is_authorized_user ? (
                      <>Authorized User - {subscriptionStatus.subscription_business_name || subscriptionStatus.subscription_client_name}</>
                    ) : (
                      <>Subscription Client</>
                    )}
                  </div>
                  <p className="text-sm text-[#134E4A] mt-1">
                    {subscriptionStatus.is_over_quota ? (
                      <span className="text-amber-700">
                        Weekly quota reached ({subscriptionStatus.bookings_used}/{subscriptionStatus.effective_limit} bookings used). 
                        You can still book using Priority service.
                      </span>
                    ) : (
                      <>
                        You have <strong>{subscriptionStatus.bookings_remaining}</strong> of {subscriptionStatus.effective_limit} bookings remaining this week.
                      </>
                    )}
                  </p>
                  {subscriptionStatus.is_authorized_user && subscriptionStatus.authorized_user_info && (
                    <p className="text-xs text-[#0D9488] mt-2">
                      Booking as: {subscriptionStatus.authorized_user_info.name} ({subscriptionStatus.authorized_user_info.role})
                    </p>
                  )}
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* For subscription clients/authorized users - show simplified options */}
                  {subscriptionStatus?.is_active && (subscriptionStatus?.is_subscription_client || subscriptionStatus?.is_authorized_user) ? (
                    <>
                      {/* Subscription Option (Included) */}
                      {!subscriptionStatus.is_over_quota && (
                        <button
                          onClick={() => {
                            const subLevel = serviceLevels.find(l => l.billing_type === 'WEEKLY_SUBSCRIPTION');
                            if (subLevel) selectServiceLevel(subLevel);
                          }}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            selectedLevel?.billing_type === 'WEEKLY_SUBSCRIPTION'
                              ? 'border-[#0D9488] bg-[#F0FDFA]' 
                              : 'border-[#E2E8F0] hover:border-[#99F6E4]'
                          }`}
                          data-testid="level-subscription"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-[#111111] flex items-center gap-2">
                                Standard Collection
                                <span className="px-2 py-0.5 bg-[#0D9488] text-white text-xs rounded-full">
                                  INCLUDED
                                </span>
                              </h3>
                              <p className="text-sm text-[#64748B]">Pickup within 7 days - part of your subscription</p>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-[#0D9488]">$0</div>
                              <div className="text-xs text-[#64748B]">No extra cost</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#64748B]">
                            <Clock className="w-4 h-4" />
                            Pickup within 7 days
                          </div>
                        </button>
                      )}

                      {/* Priority Upgrade Option */}
                      <button
                        onClick={() => {
                          const priorityLevel = serviceLevels.find(l => l.slug === 'priority' || l.name?.toLowerCase().includes('priority'));
                          if (priorityLevel) selectServiceLevel(priorityLevel);
                        }}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                          selectedLevel?.slug === 'priority' || selectedLevel?.name?.toLowerCase().includes('priority')
                            ? 'border-[#F59E0B] bg-[#FFFBEB]' 
                            : 'border-[#E2E8F0] hover:border-[#FCD34D]'
                        }`}
                        data-testid="level-priority"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-[#111111] flex items-center gap-2">
                              Priority Collection
                              <span className="px-2 py-0.5 bg-[#F59E0B] text-white text-xs rounded-full">
                                FASTER
                              </span>
                            </h3>
                            <p className="text-sm text-[#64748B]">
                              {subscriptionStatus.is_over_quota 
                                ? 'Express pickup when quota is exceeded'
                                : 'Upgrade to express pickup within 48 hours'
                              }
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-[#F59E0B]">
                              +${subscriptionStatus.priority_upgrade_cost || 30}
                            </div>
                            <div className="text-xs text-[#64748B]">Additional cost</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#64748B]">
                          <Clock className="w-4 h-4" />
                          Express pickup within 48 hours
                        </div>
                      </button>

                      {/* Show info about why subscription option is unavailable */}
                      {subscriptionStatus.is_over_quota && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                          <AlertTriangle className="w-4 h-4 inline mr-2" />
                          Your weekly quota is full. Priority collection is available at an additional cost of ${subscriptionStatus.priority_upgrade_cost || 30}.
                        </div>
                      )}
                    </>
                  ) : (
                    /* For non-subscription users - show all service levels */
                    serviceLevels.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => selectServiceLevel(level)}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                          selectedLevel?.id === level.id 
                            ? 'border-[#0D9488] bg-[#F0FDFA]' 
                            : 'border-[#E2E8F0] hover:border-[#99F6E4]'
                        }`}
                        data-testid={`level-${level.slug}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-[#111111]">{level.name}</h3>
                            <p className="text-sm text-[#64748B]">{level.description}</p>
                          </div>
                          <div className="text-right">
                            {level.billing_type === 'WEEKLY_SUBSCRIPTION' ? (
                              <div>
                                <div className="text-lg font-bold text-[#0D9488]">${level.weekly_price}/week</div>
                                <div className="text-xs text-[#64748B]">3-month term</div>
                              </div>
                            ) : (
                              <div className="text-lg font-bold text-[#0D9488]">${level.base_price}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#64748B]">
                          <Clock className="w-4 h-4" />
                          {level.sla_label}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {level.features?.slice(0, 3).map((feature, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#F1F5F9] rounded text-xs text-[#475569]">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={goBack} className="border-[#E2E8F0]">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                  onClick={handleSelectLevel}
                  disabled={!selectedLevel || checkingSubscription}
                  data-testid="continue-level-btn"
                >
                  {checkingSubscription ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Customer/Site Details */}
        {step === 3 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Customer Contact Details</h2>
                  <p className="text-sm text-[#64748B]">We'll coordinate directly with your customer to arrange collection</p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-[#F0F9FF] border border-[#BFDBFE] rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#1E40AF]">
                    <strong>Customer</strong> = the homeowner or person at the site where we pick up the waste. 
                    Transa will contact them directly to coordinate timing.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Customer Name *</label>
                  <Input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => handleChange('customerName', e.target.value)}
                    placeholder="Customer's full name"
                    className="border-[#E2E8F0] h-12"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Customer Phone *</label>
                  <Input
                    type="tel"
                    value={formData.customerMobile}
                    onChange={(e) => handleChange('customerMobile', e.target.value)}
                    placeholder="Customer's phone number"
                    className="border-[#E2E8F0] h-12"
                    data-testid="customer-mobile-input"
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-[#111111] mb-2">Site Address *</label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={formData.siteAddress}
                      onChange={(e) => handleAddressInput(e.target.value)}
                      placeholder="Start typing address..."
                      className="border-[#E2E8F0] h-12"
                      data-testid="site-address-input"
                    />
                    {isLoadingAddresses && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#64748B]" />
                      </div>
                    )}
                  </div>
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {addressSuggestions.map((suggestion, idx) => (
                        <button
                          key={suggestion.place_id || idx}
                          className="w-full px-4 py-3 text-left hover:bg-[#F8FAFC] text-sm flex items-center gap-2 border-b border-[#F1F5F9] last:border-0"
                          onClick={() => selectAddress(suggestion)}
                        >
                          <MapPin className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
                          <span className="text-[#111111]">{suggestion.description}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Notes / Access Instructions</label>
                  <textarea
                    value={formData.siteNotes}
                    onChange={(e) => handleChange('siteNotes', e.target.value)}
                    placeholder="Gate code, parking instructions, etc."
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D9488] min-h-[80px] text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={goBack} className="border-[#E2E8F0]">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                  onClick={() => setStep(4)}
                  disabled={!formData.customerName || !formData.customerMobile || !formData.siteAddress}
                  data-testid="continue-customer-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Pallets */}
        {step === 4 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#D97706]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Pallets on Site?</h2>
                  <p className="text-sm text-[#64748B]">Additional charges may apply</p>
                </div>
              </div>

              {/* Warning Box */}
              <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#92400E]">
                    <strong>Pallet removal is not included</strong> in standard waste pickup. 
                    If pallets are present, additional charges apply.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-lg cursor-pointer hover:bg-[#F8FAFC]">
                  <input
                    type="checkbox"
                    checked={formData.palletsPresent}
                    onChange={(e) => handleChange('palletsPresent', e.target.checked)}
                    className="w-5 h-5 rounded border-[#D1D5DB] text-[#0D9488] focus:ring-[#0D9488]"
                    data-testid="pallets-checkbox"
                  />
                  <div>
                    <div className="font-medium text-[#111111]">Yes, there are pallets on site</div>
                    <div className="text-sm text-[#64748B]">
                      {palletPricing?.pricing_mode === 'PER_PALLET' 
                        ? `$${palletPricing?.price} per pallet`
                        : `$${palletPricing?.price} flat fee`
                      }
                    </div>
                  </div>
                </label>

                {formData.palletsPresent && palletPricing?.pricing_mode === 'PER_PALLET' && (
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Number of Pallets</label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={formData.palletsQty}
                      onChange={(e) => handleChange('palletsQty', parseInt(e.target.value) || 1)}
                      className="border-[#E2E8F0] h-12 w-32"
                    />
                    <p className="text-sm text-[#64748B] mt-2">
                      Pallet surcharge: ${(palletPricing?.price * formData.palletsQty).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={goBack} className="border-[#E2E8F0]">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                  onClick={() => setStep(5)}
                  data-testid="continue-pallets-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F0FDFA] flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#111111]">Confirm Booking</h2>
                  <p className="text-sm text-[#64748B]">Review your booking details</p>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Service:</span>
                  <span className="font-medium text-[#111111]">Waste Removal</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Service Level:</span>
                  <span className="font-medium text-[#111111]">{selectedLevel?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">SLA:</span>
                  <span className="font-medium text-[#111111]">{selectedLevel?.sla_label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Client:</span>
                  <span className="font-medium text-[#111111]">{formData.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Customer:</span>
                  <span className="font-medium text-[#111111]">{formData.customerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Site:</span>
                  <span className="font-medium text-[#111111] text-right max-w-[200px]">{formData.siteAddress}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Pallets:</span>
                  <span className="font-medium text-[#111111]">
                    {formData.palletsPresent ? `Yes (${formData.palletsQty})` : 'Not included'}
                  </span>
                </div>
                <hr className="border-[#E2E8F0]" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-[#111111]">Total:</span>
                  <span className="font-bold text-[#0D9488]">
                    {selectedLevel?.billing_type === 'WEEKLY_SUBSCRIPTION' 
                      ? `$${selectedLevel.weekly_price}/week`
                      : `$${calculateTotal().toFixed(2)}`
                    }
                  </span>
                </div>
                {selectedLevel?.billing_type === 'WEEKLY_SUBSCRIPTION' && (
                  <p className="text-xs text-[#64748B]">Subscription: 3-month minimum term. Billed weekly.</p>
                )}
              </div>

              {/* Info Boxes */}
              <div className="space-y-3 mb-6">
                <div className="bg-[#F0FDFA] border border-[#99F6E4] rounded-lg p-3 text-sm text-[#134E4A]">
                  <strong>Transa will contact the customer directly</strong> to coordinate timing and access.
                </div>
                <div className="bg-[#F0FDFA] border border-[#99F6E4] rounded-lg p-3 text-sm text-[#134E4A]">
                  <strong>You'll be notified</strong> when the job is complete with photo confirmation.
                </div>
              </div>

              {/* Terms */}
              <div className="text-xs text-[#64748B] mb-6">
                By confirming this booking you accept our terms including:
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {selectedLevel?.billing_type === 'WEEKLY_SUBSCRIPTION' && (
                    <li>Subscription minimum term of 3 months</li>
                  )}
                  <li>Pallets excluded unless added (extra charges apply)</li>
                  <li>SLA: {selectedLevel?.sla_label}</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="border-[#E2E8F0]">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="confirm-booking-btn"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</>
                  ) : (
                    <>Confirm Booking <CheckCircle className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WasteBookingPage;
